import contentfulClient from './contentfulClient';
import { BlogPost, BlogListResponse, BlogFilters, IBlogPostFields } from '../types/blog';

/**
 * Helper function to resolve author name from Contentful entry
 * Handles locale-specific data and fallbacks
 */
export const resolveAuthorName = (author: any): string => {
  if (!author) {
    console.log('DEBUG resolveAuthorName: No author provided');
    return 'Unknown Author';
  }
  
  console.log('DEBUG resolveAuthorName: Author data:', {
    hasFields: !!author.fields,
    hasSys: !!author.sys,
    fields: author.fields,
    sys: author.sys
  });
  
  // Check if it's a resolved entry with fields
  if (author.fields) {
    const name = author.fields.name;
    console.log('DEBUG resolveAuthorName: Found name in fields:', name);
    return name || 'Unknown Author';
  }
  
  // If it's just a link/reference, we can't resolve the name
  if (author.sys && !author.fields) {
    console.warn('DEBUG resolveAuthorName: Author is not resolved - only sys data available:', {
      id: author.sys.id,
      contentType: author.sys.contentType?.sys?.id
    });
    return 'Unknown Author';
  }
  
  console.warn('DEBUG resolveAuthorName: Unexpected author structure:', author);
  return 'Unknown Author';
};

/**
 * Fetch blog posts from Contentful
 */
export const fetchBlogPosts = async (filters: BlogFilters = {}): Promise<BlogListResponse> => {
  try {
    console.log('DEBUG fetchBlogPosts: Starting fetch with filters:', filters);
    console.log('DEBUG fetchBlogPosts: Environment:', process.env.REACT_APP_CONTENTFUL_ENVIRONMENT);
    console.log('DEBUG fetchBlogPosts: Space ID:', process.env.REACT_APP_CONTENTFUL_SPACE_ID);
    
    const {
      tag,
      author,
      search,
      limit = 10,
      skip = 0
    } = filters;

    // Build query parameters
    const query: any = {
      content_type: 'pageBlogPost',
      limit,
      skip,
      order: '-sys.createdAt',  // Use system created date for ordering
      'sys.publishedAt[exists]': true  // Only show published posts on public blog
    };

    // Add filters if provided
    if (tag) {
      query['fields.tags[in]'] = tag;
    }

    if (author) {
      query['fields.author'] = author;
    }

    if (search) {
      query.query = search;
    }

    console.log('DEBUG fetchBlogPosts: Query being sent:', JSON.stringify(query, null, 2));
    
    const response = await contentfulClient.getEntries({
      ...query,
      include: 3  // Include linked assets and entries up to 3 levels deep
    });
    
    // Debug: Check if includes are working
    console.log('DEBUG fetchBlogPosts: Includes object:', response.includes);
    if (response.includes) {
      console.log('DEBUG fetchBlogPosts: Included Entry count:', response.includes.Entry?.length || 0);
      console.log('DEBUG fetchBlogPosts: Included Entries:', response.includes.Entry?.map(entry => ({
        id: entry.sys.id,
        contentType: entry.sys.contentType?.sys?.id,
        fields: entry.fields
      })));
    }
    
    console.log('DEBUG fetchBlogPosts: Response total:', response.total);
    console.log('DEBUG fetchBlogPosts: Response itemCount:', response.items.length);
    
    return {
      items: response.items as BlogPost[],
      total: response.total,
      skip: response.skip,
      limit: response.limit
    };
  } catch (error) {
    console.error('Error fetching blog posts:', error);
    throw error;
  }
};

/**
 * Fetch a single blog post by slug
 */
export const fetchBlogPostBySlug = async (slug: string): Promise<BlogPost | null> => {
  try {
    const response = await contentfulClient.getEntries({
      content_type: 'pageBlogPost',
      'fields.slug': slug,
      limit: 1,
      include: 3  // Include linked assets and entries
    });
    
    // Debug: Check includes for single post
    console.log('DEBUG fetchBlogPostBySlug: Includes object:', response.includes);
    if (response.includes) {
      console.log('DEBUG fetchBlogPostBySlug: Included Entry count:', response.includes.Entry?.length || 0);
      console.log('DEBUG fetchBlogPostBySlug: Included Entries:', response.includes.Entry?.map(entry => ({
        id: entry.sys.id,
        contentType: entry.sys.contentType?.sys?.id,
        fields: entry.fields
      })));
    }

    if (response.items.length > 0) {
      const post = response.items[0] as BlogPost;
      console.log('DEBUG fetchBlogPostBySlug: Post author data:', {
        hasAuthor: !!post.fields?.author,
        authorStructure: post.fields?.author
      });
      return post;
    }
    
    return null;
  } catch (error) {
    console.error('Error fetching blog post by slug:', error);
    throw error;
  }
};

/**
 * Get all unique tags from blog posts
 */
export const fetchBlogTags = async (): Promise<string[]> => {
  try {
    const response = await contentfulClient.getEntries({
      content_type: 'pageBlogPost',
      include: 3  // Include linked assets for tags fetching too
    });

    const allTags: string[] = [];
    response.items.forEach((item: any) => {
      if (item.fields?.tags && Array.isArray(item.fields.tags)) {
        allTags.push(...item.fields.tags);
      }
    });

    // Return unique tags
    return Array.from(new Set(allTags));
  } catch (error) {
    console.error('Error fetching blog tags:', error);
    throw error;
  }
};

/**
 * Format publish date for display
 */
export const formatPublishDate = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
};

